import { h, Fragment, JSX } from 'preact'
import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'
import { Message, message } from '../../../../lib/intl/Message'
import { Parti, GeografiskResultat } from '../../../domain'
import { useWidgetUrls } from '../../../routes/widget'
import { years as historicalYears, data as historicalData } from './data'

/* styles */
import styles from './OppslutningHistorisk.css'
import { FormattedNumber } from '../../../../lib/intl/FormattedNumber'

function normalize(value, minX, maxX, minY, maxY) {
  const slope = (maxY - minY) / (maxX - minX)
  return (value - minX) * slope + minY
}

type Props = {
  parti: Parti
  result?: GeografiskResultat
  widgetFooter?: JSX.Element
  valgtype: 'kommune' | 'fylke'
}

type Item = {
  year: number
  value: number
}

type TooltipType = {
  idx: number
  x: number
  y: number
  item: Item
}

function _OppslutningHistorisk({ parti, result, valgtype, widgetFooter }: Props) {
  const { historiskUrl } = useWidgetUrls()
  if (valgtype === 'fylke') {
    throw new Error('Historisk utvikling for fylkesvalget er ikke støttet')
  }

  const color = parti.farge
  const id = parti.id

  if (!historicalData[id] || historicalData[id].length === 0) {
    return null
  }

  const widgetUrl = historiskUrl(parti, valgtype)

  const thisYearsResult =
    result && result.stemmer.prosent !== undefined ? result.stemmer.prosent : undefined

  const values =
    thisYearsResult !== undefined ? [...historicalData[id], thisYearsResult] : historicalData[id]
  const years = thisYearsResult !== undefined ? [...historicalYears, 2019] : historicalYears
  const padding = {
    left: 20,
    right: 10,
    top: 5,
    bottom: 10
  }

  const numPadding = 0 // 10

  const minValue = Math.min(...values)
  const minNearestTen = Math.floor(0.1 * (minValue - numPadding)) * 10
  const min = Math.max(0, minNearestTen)

  // Legger på 5 prosentpoeng på årets verdi slik at det blir plass til tooltip
  const thisYearPadded = typeof thisYearsResult !== 'undefined' ? thisYearsResult + 5 : 5

  const maxValue = Math.max(thisYearPadded, ...values)
  const maxNearestTen = Math.ceil(0.1 * (maxValue + numPadding)) * 10
  const max = Math.min(maxNearestTen, 100)

  const interval = getInterval(min, max)

  const canvas = {
    height: 100 - padding.top - padding.bottom,
    width: 100 - padding.left - padding.right
  }

  const viewBox = `0 0 320 180`
  const yAxis = Array(Math.ceil((max - min) / interval) + 1)
    .fill(undefined)
    .map((_, idx, arr) => {
      const value = min + interval * (arr.length - idx - 1)
      const y = padding.top + normalize(idx / (arr.length - 1), 0, 1, padding.top, canvas.height)

      return (
        <Fragment>
          <line
            key={`${value}-y-axis-line`}
            data-value={max - interval * idx}
            x1={`${padding.left / 2}%`}
            x2="100%"
            y1={`${y}%`}
            y2={`${y}%`}
            stroke="#D3D3D3"
            strokeWidth={0.5}
          />
          <text key={`${value}-y-axis-text`} y={`${y}%`} x="0" text-anchor="left">
            {value} %
          </text>
        </Fragment>
      )
    })
  const xAxis = years.map((year, idx) => {
    const x = padding.left + normalize(idx / (years.length - 1), 0, 1, 0, canvas.width)
    return (
      <text x={`${x}%`} y="100%" text-anchor="middle">
        {year}
      </text>
    )
  })

  const results = values.map((value, idx) => {
    const y = padding.top + normalize(value, min, max, canvas.height, padding.top)
    const x = padding.left + normalize(idx / (values.length - 1), 0, 1, 0, canvas.width)
    const last = idx === values.length - 1

    let line: null | JSX.Element = null
    if (!last) {
      const y1 = padding.top + normalize(value, min, max, canvas.height, padding.top)
      const y2 = padding.top + normalize(values[idx + 1], min, max, canvas.height, padding.top)

      const x1 = padding.left + normalize(idx / (values.length - 1), 0, 1, 0, canvas.width)
      const x2 = padding.left + normalize((idx + 1) / (values.length - 1), 0, 1, 0, canvas.width)
      const shouldBeDashed = thisYearsResult !== undefined && idx === values.length - 2

      line = (
        <line
          key={`${y1}-${y2}`}
          r="1"
          x1={`${x1}%`}
          x2={`${x2}%`}
          y1={`${y1}%`}
          y2={`${y2}%`}
          stroke={color}
          stroke-dasharray={shouldBeDashed ? '3' : undefined}
          stroke-width="1"
        />
      )
    }

    return (
      <Fragment>
        {line}
        <circle
          key={`circle-${x}-${y}`}
          data-value={value}
          r="2"
          cx={`${x}%`}
          cy={`${y}%`}
          fill={last && thisYearsResult !== undefined ? '#fff' : color}
          stroke={last && thisYearsResult !== undefined ? color : 'none'}
        >
          <title>{value}%</title>
        </circle>
      </Fragment>
    )
  })

  const tooltip: TooltipType = {
    idx: values.length - 1,
    y: padding.top + normalize(values[values.length - 1], min, max, canvas.height, padding.top),
    x: padding.left + normalize((values.length - 1) / (values.length - 1), 0, 1, 0, canvas.width),
    item: {
      year: years[years.length - 1],
      value: values[values.length - 1]
    }
  }

  const isPrognose = result && result.resultatType === 'Prognose'

  return (
    <section
      class={styles.oppslutningEndring}
      data-widget-url={widgetUrl}
      data-valgtype={valgtype}
      data-grunndata={parti.id}
      data-modulnavn="oppslutning-historisk"
    >
      <Message as="h3" id="oppslutning-endring-tittel" values={{ partiet: parti.navn }} />
      <Message
        as="p"
        id="oppslutning-endring-beskrivelse"
        values={{ partiet: parti.navn, kortnavn: parti.kortNavn }}
        modifiers={{ pronose: isPrognose }}
      />
      <div class={styles.oppslutningEndringSvgWrapperOuter}>
        <div class={styles.oppslutningEndringSvgWrapper}>
          <svg
            class={styles.svgCanvas}
            viewBox={viewBox}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            width="100%"
          >
            <g class={styles.yAxis}>{yAxis}</g>
            <g class={styles.xAxis}>{xAxis}</g>
            <g class={styles.results}>{results}</g>
          </svg>
          {thisYearsResult !== undefined ? <Tooltip tooltip={tooltip} /> : null}
        </div>
      </div>
      {widgetFooter}
    </section>
  )
}

interface TooltipProps extends JSX.HTMLAttributes {
  tooltip: TooltipType
}
function Tooltip({ tooltip }: TooltipProps) {
  const arrow = { width: 25, height: 19 }
  const height = 70
  const width = 100
  const path = `M${width / 2} ${height}L${(width - arrow.width) / 2} ${height - arrow.height - 1}h${
    arrow.width
  }z`
  const viewBox = `0 0 ${width} ${height}`

  return (
    <svg
      key={tooltip.item.value.toFixed(1)}
      class={styles.svgTooltip}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      style={{
        left: `${tooltip.x}%`,
        top: `${tooltip.y}%`
      }}
    >
      <g fill="none" fill-rule="evenodd">
        <path fill="#4A4A4A" d={path} />
        <rect width={width} height={height - arrow.height} fill="#4A4A4A" rx="4" />
        <text class={styles.svgTooltipNumber} fill="#FFF" text-anchor="middle">
          <tspan x="50%" y="34">
            <FormattedNumber
              value={tooltip.item.value}
              options={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
            />{' '}
            %
          </tspan>
        </text>
      </g>
    </svg>
  )
}

function getInterval(min, max) {
  const diff = max - min
  if (diff > 40) {
    return 10
  } else if (diff > 20) {
    return 5
  } else {
    return 5
  }
}

export const OppslutningHistorisk = withSectionErrorBoundary(_OppslutningHistorisk)
